// 海報牆的卡片：3:4 封面、左上角日期牌、右上角狀態標記；滑鼠移入（或鍵盤聚焦、觸控裝置）時
// 封面底部浮出平台與期待度。整張卡片可點（標題按鈕的延伸點擊區域），標記與說明按鈕疊在上面仍可點。
import { fromMask, PLATFORMS, type PlatformKey } from '@dayone/core/dictionaries';
import { GameStatus } from '@dayone/core/release-date';
import { fromEpochDay } from '@dayone/core/time';
import { type CSSProperties, memo } from 'react';
import type { CatalogItem } from '../../filters/catalog.ts';
import { daysUntil } from '../../filters/timeline.ts';
import { t } from '../../i18n/zh-TW.ts';
import { Cover } from '../../ui/Cover.tsx';
import { Hint } from '../../ui/Hint.tsx';
import { Check, ICON, TriangleAlert, Zap } from '../../ui/icons/index.ts';
import { PlatformList } from '../../ui/PlatformList.tsx';
import { CalendarBadge } from '../gcal/CalendarBadge.tsx';
import { EarlyEditionChip, EditionsBadge } from './editions.tsx';

/** 日期牌：確定日期是「日＋月」；7 天內發售用強調色；不明確的日期用虛線框，hover 說明精度 */
export function DateChip({ item, today }: { item: CatalogItem; today: number }) {
  const d = item.date;
  if (d.p === 'day') {
    const days = daysUntil(item, today);
    const upcoming = !item.released || item.releaseDay;
    const isToday = days === 0 && upcoming;
    const soon = days !== null && days >= 0 && days <= 6 && upcoming;
    return (
      <span className={isToday ? 'datechip soon today' : soon ? 'datechip soon' : 'datechip'}>
        <b className="num">{isToday ? t.card.chipToday : d.d}</b>
        <small>{t.card.chipMonth(d.m)}</small>
      </span>
    );
  }
  const main = d.p === 'month' ? t.card.chipMonth(d.m) : d.p === 'quarter' ? `Q${d.q}` : d.p === 'year' ? d.y : 'TBA';
  const sub = d.p === 'month' || d.p === 'quarter' ? d.y : t.card.chipUndated;
  return (
    <Hint content={`${t.date.precision[d.p]}：${t.date.precisionHint[d.p]}`}>
      <button type="button" className="datechip fuzzy" aria-label={t.date.precision[d.p]}>
        <b className="num">{main}</b>
        <small className="num">{sub}</small>
      </button>
    </Hint>
  );
}

interface Props {
  item: CatalogItem;
  /** 使用者時區的今天 */
  today: number;
  onOpen: (id: string) => void;
}

export const PosterCard = memo(function PosterCard({ item, today, onOpen }: Props) {
  const { row } = item;
  const platforms = fromMask(PLATFORMS, row.p) as PlatformKey[];
  const titleId = `title-${row.id}`;
  const showReleased = item.released && !item.releaseDay;
  return (
    <article
      className="card poster"
      aria-labelledby={titleId}
      data-game={row.id}
      style={{ '--vt-name': `card-${row.id}` } as CSSProperties}
    >
      <div className="poster-art">
        <Cover id={row.id} name={row.n} cover={row.c} size="poster" />
        <DateChip item={item} today={today} />
        <div className="poster-flags">
          <CalendarBadge row={row} currentYear={fromEpochDay(today).y} />
          {!item.released && row.st === GameStatus.earlyAccess && (
            <Hint content={t.card.earlyAccessUpcomingHint}>
              <button type="button" className="badge badge-accent">
                <Zap {...ICON} />
                {t.card.earlyAccessNow}
              </button>
            </Hint>
          )}
          {showReleased && row.st === GameStatus.earlyAccess && (
            <span className="badge badge-accent" title={t.card.earlyAccess}>
              <Zap {...ICON} />
              {t.card.earlyAccess}
            </span>
          )}
          {showReleased && row.st !== GameStatus.earlyAccess && (
            <span className="badge badge-ok">
              <Check {...ICON} />
              {t.card.released}
            </span>
          )}
          {item.stale && (
            <Hint content={t.card.staleHint}>
              <button type="button" className="badge badge-warn badge-icon" aria-label={t.card.stale}>
                <TriangleAlert {...ICON} />
              </button>
            </Hint>
          )}
          {row.cf === 1 && (
            <Hint content={t.card.conflictHint}>
              <button type="button" className="badge badge-warn badge-icon" aria-label={t.card.conflict}>
                <TriangleAlert {...ICON} />
              </button>
            </Hint>
          )}
        </div>
        <div className="poster-reveal">
          <PlatformList platforms={platforms} />
          {!item.released && item.hypePct !== undefined && (
            <Hint content={t.card.hypeHint(item.hypePct, row.hy ?? 0)}>
              <button type="button" className="hype" aria-label={`${t.card.hype} ${item.hypePct}`}>
                <span className="hype-label">{t.card.hype}</span>
                <span className="hype-bar" aria-hidden="true">
                  <span className="hype-fill" style={{ width: `${Math.max(4, item.hypePct)}%` }} />
                </span>
                <span className="hype-value num">{item.hypePct}</span>
              </button>
            </Hint>
          )}
        </div>
      </div>
      <h3 id={titleId} className="card-title">
        <button type="button" className="card-link" onClick={() => onOpen(row.id)} aria-label={t.detail.open(row.n)}>
          {row.n}
        </button>
      </h3>
      {row.z && <p className="card-subtitle">{row.z}</p>}
      {row.ed && (
        <div className="poster-extra">
          <EditionsBadge row={row} currentYear={fromEpochDay(today).y} />
          <EarlyEditionChip item={item} />
        </div>
      )}
    </article>
  );
});
